import React, { useContext, useState } from "react";
import { FavInboxContext, waitForJobOnGrid } from "../context/favInbox.context";
import JobTile from "../screens/schedule/JobTile";
import { needsPersonText } from "../screens/jev/jevViewerModel";
import { jobKey, isoToMdy, inboxNeedsPerson, modDropId, projectStartIso } from "../utility/favInbox";

// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
// FAV Inbox (#630) — the left pane's Inbox tab: FAV's bookings at this office that
// nobody has dealt with yet. Plan + decisions: docs/fav-inbox.md.
//
// Shown as the "Notifications" tab (Leland 2026-10-26; the code keeps the Inbox name).
// One list (Leland 2026-10-26): FAV 2.0's bookings WITH a crew — no sections. Clicking a booking
// jumps the grid to its week, scrolls to the job and flashes it (requestJump).
//
// Actions (Leland 2026-10-26, replacing the ⋮ menu's Unschedule / Approve / Defer — #772):
//   Mod        — (Leland 2026-10-26, replacing Clear / Clear all) opens the same scheduling
//                window a drag from RTS onto the grid opens (est. completion date + arrival
//                window + Save): the Inbox makes the drag's own three calls onto the job's
//                current cell. FIRST it jumps the grid to the job's week (like clicking the
//                card) and waits until the job is on the grid, THEN opens the window (Leland
//                2026-10-26). The FAV "would book" line is gone; Why? stays.
// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -


// One button per message (Leland 2026-10-26: "remove 3 buttons, replace with a single Clear
// button"). ⚠️ Colours are INLINE on purpose: tailwind.config.js REPLACES the colour palette,
// so text-gray-* / bg-gray-* don't exist here (white-on-white on UAT v10-13-2026-03).
const CLEAR_STYLE = {
  backgroundColor: "white",
  color: "#111827",
  border: "1px solid #d1d5db",
  borderRadius: "4px",
  padding: "1px 8px",
  fontSize: "12px",
  lineHeight: "18px",
  cursor: "pointer",
  whiteSpace: "nowrap",
};

function InboxRow({ row, project, onJump, onMod }) {
  const [modding, setModding] = useState(false);
  const mod = async () => {
    setModding(true);
    try {
      await onMod(row, project);
    } finally {
      setModding(false);
    }
  };
  const [showWhy, setShowWhy] = useState(false);
  const whyText = row.why && row.why.fav2 && row.why.fav2.why; // FAV 2.0's own explanation (#652)
  const isAdvice = row.status === "advice"; // TEMPORARY "Unscheduled" rows: nothing booked
  // FileMaker took the booking back to RTS (the customer declined): kept only so FAV 2.0's
  // answer can still be seen — off the grid, nothing to act on.
  const isCancelled = row.status === "customerCancelled";

  const personText = needsPersonText(inboxNeedsPerson(row.why)); // FAV 2.0's flag only (PRED-032)

  return (
    <div className="mb-2" data-testid="fav-inbox-row" data-job-id={row.jobId}>
      <div className="flex items-start gap-1">
        <div className="flex-1 min-w-0">
          {/* Clicking the booking jumps the grid to it (not the job window). */}
          {isCancelled ? (
            <div className="w-full text-left text-xs px-2 py-1 bg-skin-primary rounded-sm opacity-80" data-testid="fav-inbox-cancelled-label">
              {row.kellerId} · booked {isoToMdy(row.bookedDate)}
            </div>
          ) : project ? (
            <JobTile project={project} projectDetailClick={() => onJump(row)} draggable={false} favDim={false} className="bg-skin-primary" />
          ) : (
            <button type="button" className="w-full text-left text-xs px-2 py-1 bg-skin-primary rounded-sm" onClick={() => onJump(row)}>
              {row.kellerId} · {isoToMdy(row.bookedDate)}
            </button>
          )}
        </div>
        {personText && (
          <span className="text-sm" title={personText} aria-label="worth a person's check">
            👤
          </span>
        )}
        {!isAdvice && (
          <button
            type="button"
            style={CLEAR_STYLE}
            disabled={modding || !project || !modDropId(project)}
            data-testid="fav-inbox-mod"
            onClick={mod}
          >
            Mod
          </button>
        )}
      </div>
      {/* Leland 2026-10-26: no "FAV 2.0 would book …" line — just the Why? */}
      {whyText && (
        <div className="text-xs mt-0.5 opacity-80">
          <button
            type="button"
            className="underline"
            aria-expanded={showWhy}
            data-testid="fav2-why-toggle"
            onClick={() => setShowWhy(!showWhy)}
          >
            Why?
          </button>
        </div>
      )}
      {showWhy && whyText && (
        <div className="text-xs mt-1 p-2 rounded-sm bg-skin-primary whitespace-pre-line" data-testid="fav2-why">
          {whyText}
        </div>
      )}
    </div>
  );
}

const FavInboxPanel = ({ scheduleArray, setDragObject, setDragObjectInvoiceNo, setDropObject }) => {
  const { rows, loading, requestJump } = useContext(FavInboxContext);

  const projectFor = (row) => (scheduleArray || []).find((j) => jobKey(j) === row.jobId) || null;
  // Mod: the same three calls a real drag + drop makes (UnassignedProjects / ScheduleScreen
  // onDragStarted + hasBeenDropped), onto the job's own cell → schedule.context opens JobInitModal.
  // Jump to the job's week first (the same jump + flash as clicking the card), wait until the
  // job's tile is on the grid, then open. If it never shows (e.g. hidden by a filter), the
  // window still opens after the wait so Mod never silently does nothing.
  const onMod = async (row, project) => {
    const dropId = modDropId(project);
    if (!project || !dropId) return;
    requestJump({ ...row, bookedDate: projectStartIso(project) || row.bookedDate });
    const onGrid = await waitForJobOnGrid(row.jobId);
    if (!onGrid) console.warn("%cFavInbox Mod: job not found on the grid — opening anyway", "color: #f97316; font-weight: bold;", row.jobId);
    setDragObject(project.id);
    setDragObjectInvoiceNo(project.invoiceNo);
    setDropObject({ id: dropId, openModal: true }); // past the grid's "same cell = do nothing" rule
  };

  // Advice-only rows have no booked date — jump to FAV 2.0's proposed start instead.
  const jumpTo = (row) =>
    requestJump(row.bookedDate ? row : { ...row, bookedDate: row.why && row.why.fav2 && row.why.fav2.startDateISO });
  const renderRows = (list) =>
    list.map((row) => <InboxRow key={row.id} row={row} project={projectFor(row)} onJump={jumpTo} onMod={onMod} />);

  if (loading) return null;

  // Leland 2026-10-26: one plain list — FAV 2.0's bookings with a crew (the context already
  // narrows `rows` to those). No sections, no accordions.
  return (
    <div className="flex-1 flex flex-col min-h-0 pt-1" data-testid="fav-inbox">
      <div className="flex-1 overflow-auto pt-1" data-testid="fav-inbox-list">
        {renderRows(rows || [])}
      </div>
    </div>
  );
};

export default FavInboxPanel;
